import { useCallback, useContext } from 'react';
import isEqual from 'lodash/isEqual';
import { isExcludeProductSelection, isIncludeProductSelection, ProductSelectionAction } from './ProductSelectionTypes';
import { LineItemKey } from '../../store/api';
import { ProductSelectionContext } from './ProductSelectionContext';
import { useDispatch } from 'react-redux';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { useTranslation } from 'react-i18next';
import { hasProductSelection } from './ProductSelectionUtils';

export const MAX_SELECT = 10000;

export const useProductSelection = () => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [state, dispatch] = useContext(ProductSelectionContext);

    const selectionModeActive = hasProductSelection(state);

    const toggleSelectionMode = useCallback(() => {
        selectionModeActive
            ? dispatch({ type: ProductSelectionAction.CLOSE })
            : dispatch({ type: ProductSelectionAction.INIT_INCLUDE_SELECTION });
    }, [dispatch, selectionModeActive]);

    const cancelSelection = useCallback(() => {
        dispatch({ type: ProductSelectionAction.CLOSE });
    }, [dispatch]);

    const selectAll = useCallback(
        (totalCount: number) => {
            if (totalCount > MAX_SELECT) {
                dispatchRedux(
                    showNotification(
                        t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }),
                        TYPE_WARNING
                    )
                );
            } else {
                dispatch({ type: ProductSelectionAction.INIT_EXCLUDE_SELECTION, totalCount });
            }
        },
        [dispatch, dispatchRedux, t]
    );

    const onSelectButtonClick = useCallback(
        (selectedProduct: LineItemKey | string) => {
            selectedProduct = mapProductIdToLineItemKey(selectedProduct);

            if (
                (isIncludeProductSelection(state) && state.included.find(item => isEqual(item, selectedProduct))) ||
                (isExcludeProductSelection(state) && !state.excluded.find(item => isEqual(item, selectedProduct)))
            ) {
                dispatch({ type: ProductSelectionAction.DESELECT, keys: [selectedProduct] });
            } else {
                dispatch({ type: ProductSelectionAction.SELECT, keys: [selectedProduct] });
            }
        },
        [dispatch, state]
    );

    return {
        productSelection: state,
        toggleSelectionMode,
        onSelectButtonClick,
        selectAll,
        cancelSelection
    };
};

export function mapProductIdToLineItemKey(item: LineItemKey | string): LineItemKey {
    return typeof item !== 'string'
        ? item
        : {
              productId: item,
              usageCondition: undefined,
              subscriptionPeriodType: undefined
          };
}
